import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useLanguage, LANGUAGE_CONFIG } from '../contexts/LanguageContext';

/**
 * Enterprise-Grade Global Language Selector
 * 
 * Features:
 * - High-contrast, clearly visible dropdown
 * - Consistent placement in top-right header
 * - Globe icon with clear label
 * - Keyboard accessible
 * - Works exactly like ReasonBot
 */
const GlobalLanguageSelector = ({ className = '' }) => {
  const { locale, setLocale, isTranslating } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      setIsOpen(!isOpen);
    }
  };

  const handleLanguageSelect = (langCode) => {
    setLocale(langCode);
    setIsOpen(false);
  };

  const currentLang = LANGUAGE_CONFIG[locale];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button - Enterprise Grade */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={isTranslating}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg
          bg-white dark:bg-slate-800
          border-2 border-slate-200 dark:border-slate-600
          hover:border-blue-400 dark:hover:border-blue-500
          hover:bg-slate-50 dark:hover:bg-slate-700
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
          transition-all duration-200
          text-sm font-medium text-slate-700 dark:text-slate-200
          shadow-sm hover:shadow
          ${isTranslating ? 'opacity-70 cursor-wait' : 'cursor-pointer'}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Select Language"
        data-testid="global-language-selector"
      >
        <Globe className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        <span className="hidden sm:inline">{currentLang?.display || 'English'}</span>
        <span className="sm:hidden">{currentLang?.nativeName?.substring(0, 3) || 'EN'}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className={`
            absolute right-0 mt-2 w-56
            bg-white dark:bg-slate-800
            border border-slate-200 dark:border-slate-600
            rounded-xl shadow-xl
            z-[9999]
            overflow-hidden
            animate-in fade-in-0 zoom-in-95
          `}
          role="listbox"
          aria-label="Available Languages"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600">
            <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Select Language
            </p>
          </div>

          {/* Language Options */}
          <div className="py-2 max-h-[320px] overflow-y-auto">
            {Object.entries(LANGUAGE_CONFIG).map(([code, config]) => {
              const isSelected = locale === code;
              return (
                <button
                  key={code}
                  onClick={() => handleLanguageSelect(code)}
                  className={`
                    w-full flex items-center justify-between px-4 py-2.5
                    text-left transition-colors duration-150
                    ${isSelected 
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                      : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                    }
                  `}
                  role="option"
                  aria-selected={isSelected}
                  data-testid={`language-option-${code}`}
                >
                  <div className="flex items-center gap-3">
                    <span 
                      className="text-base"
                      style={{ fontFamily: config.font }}
                    >
                      {config.nativeName}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      ({config.display})
                    </span>
                  </div>
                  {isSelected && (
                    <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-700 border-t border-slate-200 dark:border-slate-600">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Downloads remain in English
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalLanguageSelector;
